/**
 * @fileoverview useMenuTitle - Menu Title State Management Hook
 *
 * Provides global state management for the current menu/page title.
 * Persists the title to sessionStorage for consistency across page reloads
 * and synchronizes across browser tabs.
 *
 * @module hooks/useMenuTitleContext
 */

import React, { createContext, useState, useContext, useEffect } from 'react';

/**
 * Menu title context value interface
 * @interface MenuTitleContextProps
 */
interface MenuTitleContextProps {
  /** Current menu/page title */
  menuTitle: string;
  /** Function to update the menu title */
  updateMenuTitle: (title: string) => void;
}

/**
 * Context for menu title state management
 * @internal
 */
const MenuTitleContext = createContext<MenuTitleContextProps | undefined>(undefined);

/**
 * Hook to access and update the current menu/page title.
 *
 * The title is persisted to sessionStorage and synchronized across tabs.
 * Useful for breadcrumbs, page headers, and navigation state.
 *
 * @returns {MenuTitleContextProps} Menu title state and updater
 * @returns {string} returns.menuTitle - Current menu title
 * @returns {Function} returns.updateMenuTitle - Function to update the title
 *
 * @throws {Error} If used outside of MenuTitleProvider
 *
 * @example
 * // Reading the menu title in a header
 * function PageHeader() {
 *   const { menuTitle } = useMenuTitle();
 *   return <h1>{menuTitle || 'Dashboard'}</h1>;
 * }
 *
 * @example
 * // Updating menu title on page mount
 * function SettingsPage() {
 *   const { updateMenuTitle } = useMenuTitle();
 *
 *   useEffect(() => {
 *     updateMenuTitle('Configurações');
 *   }, [updateMenuTitle]);
 *
 *   return <div>Settings content...</div>;
 * }
 *
 * @example
 * // Using with navigation
 * function NavItem({ title, path }: { title: string; path: string }) {
 *   const { updateMenuTitle } = useMenuTitle();
 *   const navigate = useNavigate();
 *
 *   const handleClick = () => {
 *     updateMenuTitle(title);
 *     navigate(path);
 *   };
 *
 *   return <button onClick={handleClick}>{title}</button>;
 * }
 */
export const useMenuTitle = (): MenuTitleContextProps => {
  const context = useContext(MenuTitleContext);
  if (!context) {
    throw new Error('useMenuTitle must be used within a MenuTitleProvider');
  }
  return context;
};

/**
 * Provider component for menu title state.
 * Initializes from sessionStorage and syncs changes across tabs.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap
 *
 * @example
 * // In your app root
 * function App() {
 *   return (
 *     <MenuTitleProvider>
 *       <Header />
 *       <Routes />
 *     </MenuTitleProvider>
 *   );
 * }
 */
export const MenuTitleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [menuTitle, setMenuTitle] = useState<string>(() => {
    return sessionStorage.getItem('menuTitle') || '';
  });

  const updateMenuTitle = (title: string) => {
    setMenuTitle(title);
    sessionStorage.setItem('menuTitle', title);
  };

  useEffect(() => {
    const handleStorageChange = () => {
      const storedTitle = sessionStorage.getItem('menuTitle') || '';
      if (storedTitle !== menuTitle) {
        setMenuTitle(storedTitle);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [menuTitle]);

  return (
    <MenuTitleContext.Provider value={{ menuTitle, updateMenuTitle }}>
      {children}
    </MenuTitleContext.Provider>
  );
};
